import { BillingContextRequestConfig, useStorytellClient } from "@storytell/client";
import type { BillingContextResponse } from "@storytell/types";
import { type Accessor, createResource, startTransition } from "solid-js";

export type ProjectBillingService = ReturnType<typeof createProjectBillingService>;

interface ProjectBillingServiceDeps {
  authReady: Accessor<boolean>;
  authGuest: Accessor<boolean>;
  authToken: Accessor<string | undefined>;
  projectId: Accessor<string | undefined>;
}

/**
 * The paying org's balance for the current project, held once for the app so a refresh after each
 * answer sends one request rather than one per credit gate and balance display.
 */
export const createProjectBillingService = (deps: ProjectBillingServiceDeps) => {
  const client = useStorytellClient();

  const [data, { refetch }] = createResource(
    () => {
      if (!deps.authReady()) return undefined;
      if (deps.authGuest()) return undefined;
      return deps.projectId();
    },
    async (id): Promise<BillingContextResponse | undefined> => {
      const request = BillingContextRequestConfig.withBearerToken(deps.authToken());
      // The global config only stamps X-Project-Id when the URL expresses
      // that scope (URL-strict). On the billing page the URL has no
      // projectId, so we feed the resolved active project id through the
      // override path so the backend's ProjectScoped middleware sees it.
      const result = await client.fetch(request, undefined, undefined, { projectIdOverride: id });
      if (result.isErr()) {
        return undefined;
      }
      return result.value;
    },
    { initialValue: undefined }
  );

  return {
    data,
    // Always a transition: every reader shares this resource, so a bare refetch would suspend each
    // of their boundaries — the global modals' included — until the request returns.
    refetch: () => {
      void startTransition(() => void refetch());
    },
  };
};
